import { ReactNode } from 'react';
import {
    BuildingStorefrontIcon,
    CalendarDaysIcon,
    ChartBarIcon,
    ClipboardDocumentListIcon,
    CogIcon,
    DocumentDuplicateIcon,
    DocumentTextIcon,
    FolderIcon,
    HeartIcon,
    IdentificationIcon,
    InboxArrowDownIcon,
    InboxStackIcon,
    LifebuoyIcon,
    LinkIcon,
    MegaphoneIcon,
    NewspaperIcon,
    PaperAirplaneIcon,
    SparklesIcon,
    Squares2X2Icon,
    UserGroupIcon,
} from '@heroicons/react/24/outline';
import { getTemplatesMenuItems } from '../../../pages/super-admin/templates/templatesMenu';
import { getConfigMenuItems } from '../../../pages/super-admin/config/configMenu';
import { getIaMenuItems } from '../../../pages/super-admin/ai/iaMenu';
import { STATS_TABS } from '../../../pages/super-admin/analytics/statsTabs';
import { StaffAccess } from './staffAccess';
import { STAFF_QUEUE_BY_KEY, StaffQueueCounts, StaffQueueKey, TO_CLASSIFY_KEYS } from './staffQueues';
import { STAFF_HOME } from './useSuperAdminMode';

export interface StaffNavItem {
    key: string;
    /** Peut porter `?tab=` : deux entrées peuvent ouvrir le même écran sur deux onglets. */
    link: string;
    icon: ReactNode;
    /** Clé i18n du namespace `navigation`. */
    labelKey: string;
    access: StaffAccess;
    /**
     * Garde de la route quand l'écran est partagé et plus large que l'entrée :
     * « Clients » et « Prospects » ouvrent la vue CRM, ouverte à tout compte CRM,
     * qui n'affiche ensuite que les onglets permis. Par défaut : `access`.
     */
    routeAccess?: StaffAccess;
    /**
     * Préfixes de chemin rattachés à l'entrée. Sans eux, une fiche (client,
     * prospect, salon) ou une sous-page n'allume aucune entrée du menu.
     */
    matches?: string[];
    /** Chemins rattachés sur leur seule valeur exacte (pas leurs sous-pages). */
    matchesExact?: string[];
    /** L'entrée ne s'allume que sur son lien exact (en plus des chemins rattachés). */
    exact?: boolean;
    /**
     * Files dont la pastille s'affiche sur l'entrée (voir `staffQueues.ts`),
     * additionnées. Une file hors des droits n'est pas chargée, donc ne compte pas.
     */
    queues?: StaffQueueKey[];
}

export interface StaffNavSection {
    key: string;
    /** Sans libellé : entrées de tête, sans séparateur de section. */
    labelKey?: string;
    items: StaffNavItem[];
}

const icon = (Icon: typeof Squares2X2Icon) => <Icon className="w-6 h-6"/>;
const linksOf = (items: { link?: string; to?: string }[]) => items.map((item) => (item.link ?? item.to)!);

const TO_CLASSIFY_LINKS = TO_CLASSIFY_KEYS.map((key) => STAFF_QUEUE_BY_KEY[key].link);

/**
 * Table du menu back-office : la seule source de ses entrées, de leurs droits,
 * de leur état actif et de leurs pastilles. Les sections suivent les métiers de
 * l'équipe ; les URL, elles, n'ont pas bougé (liens d'e-mails, agents, favoris).
 *
 * Chaque `link` doit avoir une règle dans `config/routePermissions.ts` qui
 * correspond à son `access` : un test le vérifie.
 */
export const STAFF_NAV: StaffNavSection[] = [
    {
        key: 'staff-home',
        items: [
            { key: STAFF_HOME, link: STAFF_HOME, icon: icon(Squares2X2Icon), labelKey: 'staff.dashboard', access: 'crm:any', exact: true },
        ],
    },
    {
        key: 'support',
        labelKey: 'sections.support',
        items: [
            {
                key: 'to-classify',
                link: TO_CLASSIFY_LINKS[0],
                icon: icon(InboxArrowDownIcon),
                labelKey: 'staff_nav.to_classify',
                access: 'crm:any',
                matches: TO_CLASSIFY_LINKS,
                queues: [...TO_CLASSIFY_KEYS],
            },
            // Assistant support IA : boîte des conversations et articles d'aide (API `super_admin`).
            { key: 'sa-support', link: '/super-admin/support', icon: icon(LifebuoyIcon), labelKey: 'super_admin.support', access: 'super_admin', matches: ['/super-admin/help-articles'], queues: ['support'] },
        ],
    },
    {
        key: 'commercial',
        labelKey: 'sections.commercial',
        items: [
            { key: 'prospects', link: '/super-admin/crm?tab=prospects', icon: icon(UserGroupIcon), labelKey: 'staff_nav.prospects', access: 'crm:prospects', routeAccess: 'crm:any', exact: true, matches: ['/super-admin/crm/prospects'] },
            // Prospection à froid réservée aux super admins (26/10/2026) : API gardée par `super_admin`.
            { key: 'crm-prospection', link: '/super-admin/crm/prospection', icon: icon(PaperAirplaneIcon), labelKey: 'crm.prospecting', access: 'super_admin', queues: ['prospection'] },
            // Sas des saisies de prestataire de salon : API gardée par `crm:prospects`.
            { key: 'crm-demandes', link: '/super-admin/crm/demandes', icon: icon(InboxStackIcon), labelKey: 'crm.demandes', access: 'crm:prospects', queues: ['demandes'] },
            { key: 'crm-events', link: '/super-admin/crm/salons', icon: icon(CalendarDaysIcon), labelKey: 'staff_nav.events', access: 'crm:prospects' },
            // API gardée par le middleware `super_admin`.
            { key: 'crm-lead-magnets', link: '/super-admin/lead-magnets', icon: icon(DocumentTextIcon), labelKey: 'crm.lead_magnets', access: 'super_admin' },
            { key: 'crm-documents', link: '/super-admin/crm/documents-partages', icon: icon(DocumentDuplicateIcon), labelKey: 'crm.shared_documents', access: 'crm:any' },
        ],
    },
    {
        key: 'clients',
        labelKey: 'sections.clients',
        items: [
            {
                key: 'crm',
                link: '/super-admin/crm',
                icon: icon(IdentificationIcon),
                labelKey: 'staff_nav.clients',
                access: 'crm:clients',
                routeAccess: 'crm:any',
                exact: true,
                matches: ['/super-admin/crm/facilities', '/super-admin/crm/email-templates'],
            },
            { key: 'crm-tasks', link: '/super-admin/crm/tasks', icon: icon(ClipboardDocumentListIcon), labelKey: 'staff_nav.tasks', access: 'crm:any', queues: ['tasks'] },
        ],
    },
    {
        key: 'content',
        labelKey: 'sections.content',
        items: [
            { key: 'sa-blog', link: '/super-admin/blog', icon: icon(NewspaperIcon), labelKey: 'super_admin.blog', access: 'super_admin', queues: ['blog'] },
            { key: 'sa-changelog', link: '/super-admin/changelog', icon: icon(MegaphoneIcon), labelKey: 'super_admin.changelog', access: 'super_admin' },
            { key: 'sa-seo', link: '/super-admin/seo/landing-backlinks', icon: icon(LinkIcon), labelKey: 'staff_nav.seo', access: 'super_admin' },
        ],
    },
    {
        key: 'platform',
        labelKey: 'sections.platform',
        items: [
            { key: 'sa-facilities', link: '/super-admin/facilities', icon: icon(BuildingStorefrontIcon), labelKey: 'super_admin.facilities', access: 'super_admin' },
            { key: 'sa-health', link: '/super-admin/flow-stats/health', icon: icon(HeartIcon), labelKey: 'staff_nav.health', access: 'super_admin' },
            // Analytics et statistiques des flows : une entrée, des onglets.
            { key: 'sa-stats', link: '/super-admin/analytics', icon: icon(ChartBarIcon), labelKey: 'staff_nav.stats', access: 'super_admin', exact: true, matchesExact: linksOf(STATS_TABS) },
            { key: 'sa-templates', link: '/super-admin/flow-templates', icon: icon(FolderIcon), labelKey: 'super_admin.templates', access: 'super_admin', matches: linksOf(getTemplatesMenuItems()) },
            { key: 'sa-ai', link: '/super-admin/config/ai', icon: icon(SparklesIcon), labelKey: 'staff_nav.ai', access: 'super_admin', matches: linksOf(getIaMenuItems()) },
            // Exacte : `/super-admin/config/ai` appartient à l'entrée IA.
            { key: 'sa-config', link: '/super-admin/config', icon: icon(CogIcon), labelKey: 'super_admin.config', access: 'super_admin', exact: true, matches: linksOf(getConfigMenuItems()) },
        ],
    },
];

const isUnder = (pathname: string, prefix: string) => pathname === prefix || pathname.startsWith(`${prefix}/`);
const tabOf = (query: string) => new URLSearchParams(query).get('tab');

/**
 * L'entrée est-elle active sur ce chemin (et cette chaîne de requête) ?
 *
 * Correspondance par segment (`/super-admin/crm` n'allume pas
 * `/super-admin/crmx`), exacte si `exact`, élargie par les chemins rattachés.
 * Sur son propre lien, l'onglet `?tab=` doit aussi correspondre : « Clients »
 * et « Prospects » ouvrent le même écran sur deux onglets.
 */
export function isStaffNavItemActive(item: StaffNavItem, pathname: string, search = ''): boolean {
    const path = pathname.length > 1 ? pathname.replace(/\/$/, '') : pathname;
    const [linkPath, linkQuery = ''] = item.link.split('?');

    const onLinkPath = item.exact ? path === linkPath : isUnder(path, linkPath);
    const onLink = onLinkPath && tabOf(linkQuery) === tabOf(search);

    return onLink
        || (item.matches ?? []).some((prefix) => isUnder(path, prefix))
        || (item.matchesExact ?? []).some((exactPath) => path === exactPath);
}

/** Éléments en attente sur l'entrée : somme de ses files chargées. */
export function staffNavItemCount(item: StaffNavItem, counts: StaffQueueCounts): number | undefined {
    if (!item.queues) return undefined;
    return item.queues.reduce((total, key) => total + (counts[key] ?? 0), 0);
}
